﻿import type { CountryCode, LocaleCode, LocalizedText } from "./types.js";

type Dictionary = Record<string, string>;

export const dictionaries: Record<LocaleCode, Dictionary> = {
  en: {
    appTitle: "Compliance Lite",
    status_pass: "Ready to use",
    status_review: "Needs review",
    status_fail: "Fix before issuing",
    severity_error: "Error",
    severity_warning: "Warning",
    severity_info: "Info",
    summary_pass_title: "The invoice draft passed the core rules.",
    summary_review_title: "The invoice draft is usable after a short review.",
    summary_fail_title: "The invoice draft is not compliant yet.",
    summary_pass_subtitle: "No blocking issues were found in the selected country pack.",
    summary_review_subtitle: "We found warning-level items that should be confirmed before issuing.",
    summary_fail_subtitle: "We found blocking issues in mandatory fields, tax logic, or totals.",
    action_add_missing_fields: "Fill in the missing mandatory fields.",
    action_fix_tax_logic: "Adjust tax status, tax IDs, or tax rates to match the selected country rules.",
    action_fix_totals: "Recalculate line amounts and totals so the numbers agree.",
    action_validate_cross_border: "Review the cross-border treatment and keep supporting evidence.",
    action_check_thresholds: "Review registration thresholds and decide whether tax registration is now required.",
    field_invoiceNumber: "invoice number",
    field_issueDate: "issue date",
    field_issueTime: "issue time",
    field_currency: "currency",
    field_sellerLegalName: "seller legal name",
    field_sellerRegistrationNumber: "seller registration number",
    field_sellerTaxNumber: "seller tax number",
    field_sellerAddress: "seller address",
    field_buyerLegalName: "buyer legal name",
    field_buyerAddress: "buyer address",
    field_buyerTaxNumber: "buyer tax number",
    field_buyerPhone: "buyer contact number",
    field_itemDescription: "line item description",
    field_itemClassificationCode: "line item classification code",
    field_itemQuantity: "line item quantity",
    field_itemUnitPrice: "line item unit price",
    field_transactionCode: "tax invoice transaction code",
    field_etaxRegistered: "approved e-Tax / e-Receipt issuer confirmation",
    field_foreignCurrencyApproval: "foreign language / currency approval",
    field_priceIncludesTaxNote: "tax-inclusive statement",
    profile_sg_standard: "Singapore GST tax invoice",
    profile_sg_simplified: "Singapore simplified tax invoice",
    profile_sg_non_registered: "Singapore commercial invoice",
    profile_my_einvoice: "Malaysia e-Invoice draft",
    profile_my_sst: "Malaysia SST invoice",
    profile_my_non_registered: "Malaysia commercial invoice",
    profile_id_pkp: "Indonesia PKP e-Faktur draft",
    profile_id_retail: "Indonesia retail tax invoice",
    profile_id_non_registered: "Indonesia commercial invoice",
    required_field_title: "Missing mandatory field",
    required_field_summary: "Add {field} before issuing the invoice.",
    required_field_action: "Enter {field} and re-run the check.",
    format_title: "Registration number format looks invalid",
    format_summary: "{field} does not match the expected format for {country}.",
    format_action: "Double-check the identifier against the official registration record.",
    tax_id_required_title: "Tax number is required",
    tax_id_required_summary: "The invoice charges {taxLabel}, so the seller tax number must be shown.",
    tax_id_required_action: "Enter the seller tax number or switch the invoice to non-registered mode.",
    tax_rate_title: "Tax rate does not match the country rule",
    tax_rate_summary: "Expected {expectedRate}% for this invoice, but found {actualRate}%.",
    tax_rate_action: "Use the standard rate, a supported zero-rate case, or remove tax if not registered.",
    total_mismatch_title: "Totals do not reconcile",
    total_mismatch_summary: "Line items and invoice totals disagree by {difference}.",
    total_mismatch_action: "Recalculate line totals, tax totals, discounts, and rounding.",
    duplicate_title: "Duplicate invoice number detected",
    duplicate_summary: "This seller already used invoice number {invoiceNumber}.",
    duplicate_action: "Use a new unique invoice number or amend the original record instead.",
    cross_border_title: "Cross-border treatment needs supporting evidence",
    cross_border_summary: "The selected zero-rate or export treatment needs documentary support.",
    cross_border_action: "Keep a written contract, overseas-customer evidence, and payment proof where required.",
    threshold_near_title: "Registration threshold is getting close",
    threshold_near_summary: "{taxLabel} threshold is {threshold}; current turnover is {current}.",
    threshold_near_action: "Monitor turnover frequently and prepare registration details in advance.",
    threshold_above_title: "Registration threshold has been exceeded",
    threshold_above_summary: "{taxLabel} threshold is {threshold}; current turnover is {current}.",
    threshold_above_action: "Start the registration workflow and issue invoices using the registered-tax profile.",
    threshold_future_title: "Projected turnover creates a future registration obligation",
    threshold_future_summary: "{taxLabel} threshold is {threshold}; projected turnover is {projected}.",
    threshold_future_action: "Prepare supporting documents and plan the effective registration date.",
    note_title: "Country-specific reminder",
    note_summary: "{message}",
    note_action: "Review the note and confirm the invoice wording matches the official requirement.",
    threshold_status_below: "Below threshold",
    threshold_status_near: "Near threshold",
    threshold_status_above: "Above threshold",
    threshold_status_future: "Projected liability",
    threshold_status_na: "Not applicable",
    country_SG: "Singapore",
    country_MY: "Malaysia",
    country_ID: "Indonesia",
    country_TH: "Thailand",
    profile_th_vat: "Thailand VAT tax invoice",
    profile_th_non_registered: "Thailand commercial invoice",
    profile_th_etax: "Thailand e-Tax invoice / e-Receipt draft",
    profile_th_abbreviated: "Thailand abbreviated tax invoice"
  },
  "zh-CN": {
    appTitle: "Compliance Lite",
    status_pass: "可以直接使用",
    status_review: "建议复核",
    status_fail: "发出前必须修正",
    severity_error: "错误",
    severity_warning: "警告",
    severity_info: "提示",
    summary_pass_title: "这份发票草稿通过了核心规则检查。",
    summary_review_title: "这份发票草稿基本可用，但建议先做一次复核。",
    summary_fail_title: "这份发票草稿当前还不合规。",
    summary_pass_subtitle: "所选国家规则包下，没有发现阻断性问题。",
    summary_review_subtitle: "我们发现了一些应在开票前确认的警告项。",
    summary_fail_subtitle: "我们发现了必填字段、税务逻辑或金额结构方面的阻断性问题。",
    action_add_missing_fields: "补齐所有缺失的必填字段。",
    action_fix_tax_logic: "按所选国家规则修正税务状态、税号或税率。",
    action_fix_totals: "重新计算行项目和汇总金额，确保数字一致。",
    action_validate_cross_border: "复核跨境税务处理，并保留支持文件。",
    action_check_thresholds: "检查注册阈值，判断是否已经触发注册义务。",
    field_invoiceNumber: "发票编号",
    field_issueDate: "开票日期",
    field_issueTime: "开票时间",
    field_currency: "币种",
    field_sellerLegalName: "卖方法定名称",
    field_sellerRegistrationNumber: "卖方注册号",
    field_sellerTaxNumber: "卖方税号",
    field_sellerAddress: "卖方地址",
    field_buyerLegalName: "买方法定名称",
    field_buyerAddress: "买方地址",
    field_buyerTaxNumber: "买方税号",
    field_buyerPhone: "买方联系电话",
    field_itemDescription: "行项目描述",
    field_itemClassificationCode: "行项目分类编码",
    field_itemQuantity: "行项目数量",
    field_itemUnitPrice: "行项目单价",
    field_transactionCode: "税务发票交易代码",
    field_etaxRegistered: "已获批准的 e-Tax / e-Receipt 发行资格确认",
    field_foreignCurrencyApproval: "外文 / 外币批准确认",
    field_priceIncludesTaxNote: "含税说明",
    profile_sg_standard: "新加坡 GST 税务发票",
    profile_sg_simplified: "新加坡简化税务发票",
    profile_sg_non_registered: "新加坡普通商业发票",
    profile_my_einvoice: "马来西亚 e-Invoice 草稿",
    profile_my_sst: "马来西亚 SST 发票",
    profile_my_non_registered: "马来西亚普通商业发票",
    profile_id_pkp: "印度尼西亚 PKP e-Faktur 草稿",
    profile_id_retail: "印度尼西亚零售税务发票",
    profile_id_non_registered: "印度尼西亚普通商业发票",
    required_field_title: "缺少必填字段",
    required_field_summary: "发票发出前必须补充 {field}。",
    required_field_action: "填写 {field} 后重新检查。",
    format_title: "注册号格式看起来不正确",
    format_summary: "{field} 与 {country} 的预期格式不匹配。",
    format_action: "请对照官方注册信息再次核对该标识。",
    tax_id_required_title: "必须展示税号",
    tax_id_required_summary: "这张发票已计收 {taxLabel}，因此必须展示卖方税号。",
    tax_id_required_action: "填写卖方税号，或改为未注册纳税模式。",
    tax_rate_title: "税率与国家规则不一致",
    tax_rate_summary: "该发票预期税率为 {expectedRate}%，当前为 {actualRate}%。",
    tax_rate_action: "请使用标准税率、受支持的零税率场景，或在未注册时移除税额。",
    total_mismatch_title: "汇总金额不一致",
    total_mismatch_summary: "行项目与发票汇总之间存在 {difference} 的差异。",
    total_mismatch_action: "重新核算行金额、税额、折扣和 rounding。",
    duplicate_title: "检测到重复发票编号",
    duplicate_summary: "该卖方已经使用过发票编号 {invoiceNumber}。",
    duplicate_action: "请使用新的唯一发票编号，或改为修订原单据。",
    cross_border_title: "跨境税务处理需要支持文件",
    cross_border_summary: "当前选择的零税率或出口处理需要文件支撑。",
    cross_border_action: "请保留书面合同、海外客户证明和付款证明等材料。",
    threshold_near_title: "注册阈值正在逼近",
    threshold_near_summary: "{taxLabel} 阈值为 {threshold}，当前营业额为 {current}。",
    threshold_near_action: "请更频繁地监控营业额，并提前准备注册资料。",
    threshold_above_title: "已超过注册阈值",
    threshold_above_summary: "{taxLabel} 阈值为 {threshold}，当前营业额为 {current}。",
    threshold_above_action: "请启动注册流程，并改用注册纳税人开票档案。",
    threshold_future_title: "预测营业额将触发未来注册义务",
    threshold_future_summary: "{taxLabel} 阈值为 {threshold}，预测营业额为 {projected}。",
    threshold_future_action: "请准备支持文件，并规划生效注册日期。",
    note_title: "国家专项提醒",
    note_summary: "{message}",
    note_action: "请核对该提醒，并确认发票措辞与官方要求一致。",
    threshold_status_below: "低于阈值",
    threshold_status_near: "接近阈值",
    threshold_status_above: "超过阈值",
    threshold_status_future: "预测将触发",
    threshold_status_na: "不适用",
    country_SG: "新加坡",
    country_MY: "马来西亚",
    country_ID: "印度尼西亚",
    country_TH: "泰国",
    profile_th_vat: "泰国 VAT 税务发票",
    profile_th_non_registered: "泰国普通商业发票",
    profile_th_etax: "泰国电子税务发票 / 电子收据草稿",
    profile_th_abbreviated: "泰国简式税务发票"
  },
  "ms-MY": {
    appTitle: "Compliance Lite",
    status_pass: "Sedia digunakan",
    status_review: "Perlu semakan",
    status_fail: "Betulkan sebelum dikeluarkan",
    severity_error: "Ralat",
    severity_warning: "Amaran",
    severity_info: "Makluman",
    summary_pass_title: "Draf invois ini lulus semakan teras.",
    summary_review_title: "Draf invois ini boleh digunakan selepas semakan ringkas.",
    summary_fail_title: "Draf invois ini masih belum patuh.",
    summary_pass_subtitle: "Tiada isu kritikal ditemui dalam pakej negara yang dipilih.",
    summary_review_subtitle: "Kami menemui item amaran yang wajar disahkan sebelum invois dikeluarkan.",
    summary_fail_subtitle: "Kami menemui isu kritikal pada medan wajib, logik cukai, atau jumlah.",
    action_add_missing_fields: "Lengkapkan semua medan wajib yang masih kosong.",
    action_fix_tax_logic: "Laraskan status cukai, nombor cukai, atau kadar cukai mengikut peraturan negara.",
    action_fix_totals: "Kira semula amaun baris dan jumlah supaya semua nombor sepadan.",
    action_validate_cross_border: "Semak rawatan transaksi rentas sempadan dan simpan dokumen sokongan.",
    action_check_thresholds: "Semak ambang pendaftaran dan tentukan sama ada pendaftaran kini diwajibkan.",
    field_invoiceNumber: "nombor invois",
    field_issueDate: "tarikh invois",
    field_issueTime: "masa invois",
    field_currency: "mata wang",
    field_sellerLegalName: "nama sah penjual",
    field_sellerRegistrationNumber: "nombor pendaftaran penjual",
    field_sellerTaxNumber: "nombor cukai penjual",
    field_sellerAddress: "alamat penjual",
    field_buyerLegalName: "nama sah pembeli",
    field_buyerAddress: "alamat pembeli",
    field_buyerTaxNumber: "nombor cukai pembeli",
    field_buyerPhone: "nombor telefon pembeli",
    field_itemDescription: "keterangan item",
    field_itemClassificationCode: "kod klasifikasi item",
    field_itemQuantity: "kuantiti item",
    field_itemUnitPrice: "harga seunit item",
    field_transactionCode: "kod transaksi invois cukai",
    field_etaxRegistered: "pengesahan pengeluar e-Tax / e-Receipt yang diluluskan",
    field_foreignCurrencyApproval: "kelulusan bahasa / mata wang asing",
    field_priceIncludesTaxNote: "nota harga termasuk cukai",
    profile_sg_standard: "Invois cukai GST Singapura",
    profile_sg_simplified: "Invois cukai ringkas Singapura",
    profile_sg_non_registered: "Invois komersial Singapura",
    profile_my_einvoice: "Draf e-Invois Malaysia",
    profile_my_sst: "Invois SST Malaysia",
    profile_my_non_registered: "Invois komersial Malaysia",
    profile_id_pkp: "Draf e-Faktur PKP Indonesia",
    profile_id_retail: "Faktur cukai runcit Indonesia",
    profile_id_non_registered: "Invois komersial Indonesia",
    required_field_title: "Medan wajib tiada",
    required_field_summary: "Tambah {field} sebelum invois dikeluarkan.",
    required_field_action: "Isikan {field} dan jalankan semakan semula.",
    format_title: "Format nombor pendaftaran nampak tidak sah",
    format_summary: "{field} tidak sepadan dengan format jangkaan untuk {country}.",
    format_action: "Semak semula pengecam ini dengan rekod pendaftaran rasmi.",
    tax_id_required_title: "Nombor cukai diperlukan",
    tax_id_required_summary: "Invois ini mengenakan {taxLabel}, jadi nombor cukai penjual mesti dipaparkan.",
    tax_id_required_action: "Masukkan nombor cukai penjual atau tukar kepada mod tidak berdaftar.",
    tax_rate_title: "Kadar cukai tidak sepadan dengan peraturan negara",
    tax_rate_summary: "Kadar jangkaan ialah {expectedRate}%, tetapi yang dijumpai ialah {actualRate}%.",
    tax_rate_action: "Gunakan kadar standard, kes kadar sifar yang dibenarkan, atau buang cukai jika tidak berdaftar.",
    total_mismatch_title: "Jumlah tidak sepadan",
    total_mismatch_summary: "Item baris dan jumlah invois berbeza sebanyak {difference}.",
    total_mismatch_action: "Kira semula jumlah item, jumlah cukai, diskaun dan pembundaran.",
    duplicate_title: "Nombor invois pendua dikesan",
    duplicate_summary: "Penjual ini telah menggunakan nombor invois {invoiceNumber}.",
    duplicate_action: "Gunakan nombor invois baharu yang unik atau pindahkan kepada pindaan rekod asal.",
    cross_border_title: "Rawatan rentas sempadan memerlukan bukti sokongan",
    cross_border_summary: "Rawatan kadar sifar atau eksport yang dipilih memerlukan dokumen sokongan.",
    cross_border_action: "Simpan kontrak bertulis, bukti pelanggan luar negara dan bukti bayaran jika berkenaan.",
    threshold_near_title: "Ambang pendaftaran semakin hampir",
    threshold_near_summary: "Ambang {taxLabel} ialah {threshold}; perolehan semasa ialah {current}.",
    threshold_near_action: "Pantau perolehan dengan lebih kerap dan sediakan butiran pendaftaran lebih awal.",
    threshold_above_title: "Ambang pendaftaran telah dilepasi",
    threshold_above_summary: "Ambang {taxLabel} ialah {threshold}; perolehan semasa ialah {current}.",
    threshold_above_action: "Mulakan proses pendaftaran dan gunakan profil invois berdaftar.",
    threshold_future_title: "Unjuran perolehan mewujudkan obligasi pendaftaran akan datang",
    threshold_future_summary: "Ambang {taxLabel} ialah {threshold}; unjuran perolehan ialah {projected}.",
    threshold_future_action: "Sediakan dokumen sokongan dan rancang tarikh kuat kuasa pendaftaran.",
    note_title: "Peringatan khusus negara",
    note_summary: "{message}",
    note_action: "Semak nota ini dan pastikan wording invois sepadan dengan keperluan rasmi.",
    threshold_status_below: "Di bawah ambang",
    threshold_status_near: "Hampir ambang",
    threshold_status_above: "Melepasi ambang",
    threshold_status_future: "Liabiliti masa depan",
    threshold_status_na: "Tidak terpakai",
    country_SG: "Singapura",
    country_MY: "Malaysia",
    country_ID: "Indonesia",
    country_TH: "Thailand",
    profile_th_vat: "Invois cukai VAT Thailand",
    profile_th_non_registered: "Invois komersial Thailand",
    profile_th_etax: "Draf e-Tax Invoice / e-Receipt Thailand",
    profile_th_abbreviated: "Invois cukai ringkas Thailand"
  },
  "id-ID": {
    appTitle: "Compliance Lite",
    status_pass: "Siap digunakan",
    status_review: "Perlu ditinjau",
    status_fail: "Perbaiki sebelum diterbitkan",
    severity_error: "Error",
    severity_warning: "Peringatan",
    severity_info: "Info",
    summary_pass_title: "Draf invoice ini lolos pemeriksaan inti.",
    summary_review_title: "Draf invoice ini dapat dipakai setelah peninjauan singkat.",
    summary_fail_title: "Draf invoice ini belum patuh.",
    summary_pass_subtitle: "Tidak ditemukan masalah pemblokir dalam paket negara yang dipilih.",
    summary_review_subtitle: "Kami menemukan item peringatan yang sebaiknya dikonfirmasi sebelum invoice diterbitkan.",
    summary_fail_subtitle: "Kami menemukan masalah pemblokir pada field wajib, logika pajak, atau total.",
    action_add_missing_fields: "Lengkapi semua field wajib yang masih kosong.",
    action_fix_tax_logic: "Sesuaikan status pajak, nomor pajak, atau tarif pajak dengan aturan negara.",
    action_fix_totals: "Hitung ulang baris dan total agar semua angka selaras.",
    action_validate_cross_border: "Tinjau perlakuan lintas batas dan simpan dokumen pendukung.",
    action_check_thresholds: "Tinjau ambang pendaftaran dan tentukan apakah pendaftaran kini wajib.",
    field_invoiceNumber: "nomor invoice",
    field_issueDate: "tanggal invoice",
    field_issueTime: "waktu invoice",
    field_currency: "mata uang",
    field_sellerLegalName: "nama legal penjual",
    field_sellerRegistrationNumber: "nomor registrasi penjual",
    field_sellerTaxNumber: "nomor pajak penjual",
    field_sellerAddress: "alamat penjual",
    field_buyerLegalName: "nama legal pembeli",
    field_buyerAddress: "alamat pembeli",
    field_buyerTaxNumber: "nomor pajak pembeli",
    field_buyerPhone: "nomor kontak pembeli",
    field_itemDescription: "deskripsi item",
    field_itemClassificationCode: "kode klasifikasi item",
    field_itemQuantity: "kuantitas item",
    field_itemUnitPrice: "harga satuan item",
    field_transactionCode: "kode transaksi faktur pajak",
    field_etaxRegistered: "konfirmasi penerbit e-Tax / e-Receipt yang disetujui",
    field_foreignCurrencyApproval: "persetujuan bahasa / mata uang asing",
    field_priceIncludesTaxNote: "catatan harga termasuk pajak",
    profile_sg_standard: "Faktur pajak GST Singapura",
    profile_sg_simplified: "Faktur pajak sederhana Singapura",
    profile_sg_non_registered: "Invoice komersial Singapura",
    profile_my_einvoice: "Draf e-Invoice Malaysia",
    profile_my_sst: "Invoice SST Malaysia",
    profile_my_non_registered: "Invoice komersial Malaysia",
    profile_id_pkp: "Draf e-Faktur PKP Indonesia",
    profile_id_retail: "Faktur pajak ritel Indonesia",
    profile_id_non_registered: "Invoice komersial Indonesia",
    required_field_title: "Field wajib belum ada",
    required_field_summary: "Tambahkan {field} sebelum invoice diterbitkan.",
    required_field_action: "Isi {field} lalu jalankan pemeriksaan ulang.",
    format_title: "Format nomor registrasi tampak tidak valid",
    format_summary: "{field} tidak sesuai dengan format yang diharapkan untuk {country}.",
    format_action: "Periksa kembali identitas ini terhadap catatan registrasi resmi.",
    tax_id_required_title: "Nomor pajak wajib dicantumkan",
    tax_id_required_summary: "Invoice ini memungut {taxLabel}, jadi nomor pajak penjual harus ditampilkan.",
    tax_id_required_action: "Masukkan nomor pajak penjual atau ubah ke mode tidak terdaftar.",
    tax_rate_title: "Tarif pajak tidak sesuai dengan aturan negara",
    tax_rate_summary: "Tarif yang diharapkan adalah {expectedRate}%, tetapi yang ditemukan {actualRate}%.",
    tax_rate_action: "Gunakan tarif standar, kasus tarif 0% yang diizinkan, atau hilangkan pajak jika belum terdaftar.",
    total_mismatch_title: "Total tidak selaras",
    total_mismatch_summary: "Item baris dan total invoice berbeda sebesar {difference}.",
    total_mismatch_action: "Hitung ulang total baris, total pajak, diskon, dan pembulatan.",
    duplicate_title: "Nomor invoice duplikat terdeteksi",
    duplicate_summary: "Penjual ini sudah memakai nomor invoice {invoiceNumber}.",
    duplicate_action: "Gunakan nomor invoice baru yang unik atau ubah rekam aslinya.",
    cross_border_title: "Perlakuan lintas batas memerlukan bukti pendukung",
    cross_border_summary: "Perlakuan ekspor atau tarif 0% yang dipilih memerlukan bukti dokumen.",
    cross_border_action: "Simpan kontrak tertulis, bukti pelanggan luar negeri, dan bukti pembayaran bila diperlukan.",
    threshold_near_title: "Ambang pendaftaran semakin dekat",
    threshold_near_summary: "Ambang {taxLabel} adalah {threshold}; omzet saat ini {current}.",
    threshold_near_action: "Pantau omzet lebih sering dan siapkan data pendaftaran lebih awal.",
    threshold_above_title: "Ambang pendaftaran telah terlampaui",
    threshold_above_summary: "Ambang {taxLabel} adalah {threshold}; omzet saat ini {current}.",
    threshold_above_action: "Mulai proses pendaftaran dan gunakan profil invoice wajib pajak terdaftar.",
    threshold_future_title: "Proyeksi omzet menimbulkan kewajiban pendaftaran ke depan",
    threshold_future_summary: "Ambang {taxLabel} adalah {threshold}; proyeksi omzet {projected}.",
    threshold_future_action: "Siapkan dokumen pendukung dan rencanakan tanggal efektif pendaftaran.",
    note_title: "Pengingat khusus negara",
    note_summary: "{message}",
    note_action: "Tinjau catatan ini dan pastikan wording invoice sesuai ketentuan resmi.",
    threshold_status_below: "Di bawah ambang",
    threshold_status_near: "Mendekati ambang",
    threshold_status_above: "Di atas ambang",
    threshold_status_future: "Kewajiban proyeksi",
    threshold_status_na: "Tidak berlaku",
    country_SG: "Singapura",
    country_MY: "Malaysia",
    country_ID: "Indonesia",
    country_TH: "Thailand",
    profile_th_vat: "Invoice pajak VAT Thailand",
    profile_th_non_registered: "Invoice komersial Thailand",
    profile_th_etax: "Draf e-Tax Invoice / e-Receipt Thailand",
    profile_th_abbreviated: "Invoice pajak ringkas Thailand"
  },
  "th-TH": {
    appTitle: "Compliance Lite",
    status_pass: "พร้อมใช้งาน",
    status_review: "ควรตรวจทาน",
    status_fail: "ต้องแก้ไขก่อนออกเอกสาร",
    severity_error: "ข้อผิดพลาด",
    severity_warning: "คำเตือน",
    severity_info: "ข้อมูล",
    summary_pass_title: "ร่างเอกสารนี้ผ่านกฎหลักแล้ว",
    summary_review_title: "ร่างเอกสารนี้ใช้งานได้หลังตรวจทานสั้น ๆ",
    summary_fail_title: "ร่างเอกสารนี้ยังไม่สอดคล้องกับกฎ",
    summary_pass_subtitle: "ไม่พบประเด็นที่บล็อกการใช้งานในชุดกฎของประเทศที่เลือก",
    summary_review_subtitle: "พบรายการระดับคำเตือนที่ควรยืนยันก่อนออกเอกสาร",
    summary_fail_subtitle: "พบปัญหาในฟิลด์บังคับ ตรรกะภาษี หรือยอดรวม",
    action_add_missing_fields: "กรอกฟิลด์บังคับที่ยังขาดให้ครบ",
    action_fix_tax_logic: "ปรับสถานะภาษี เลขภาษี หรืออัตราภาษีให้ตรงกับกฎของประเทศที่เลือก",
    action_fix_totals: "คำนวณยอดแต่ละรายการและยอดรวมใหม่ให้ตรงกัน",
    action_validate_cross_border: "ตรวจทานธุรกรรมข้ามพรมแดนและเก็บหลักฐานประกอบ",
    action_check_thresholds: "ตรวจสอบเกณฑ์การจดทะเบียนว่าถึงจุดที่ต้องจดทะเบียนแล้วหรือไม่",
    field_invoiceNumber: "เลขที่เอกสาร",
    field_issueDate: "วันที่ออกเอกสาร",
    field_issueTime: "เวลาออกเอกสาร",
    field_currency: "สกุลเงิน",
    field_sellerLegalName: "ชื่อผู้ขาย",
    field_sellerRegistrationNumber: "เลขทะเบียนผู้ขาย",
    field_sellerTaxNumber: "เลขประจำตัวผู้เสียภาษีผู้ขาย",
    field_sellerAddress: "ที่อยู่ผู้ขาย",
    field_buyerLegalName: "ชื่อผู้ซื้อ",
    field_buyerAddress: "ที่อยู่ผู้ซื้อ",
    field_buyerTaxNumber: "เลขประจำตัวผู้เสียภาษีผู้ซื้อ",
    field_buyerPhone: "เบอร์ผู้ซื้อ",
    field_itemDescription: "รายละเอียดรายการ",
    field_itemClassificationCode: "รหัสจัดประเภทสินค้า/บริการ",
    field_itemQuantity: "จำนวน",
    field_itemUnitPrice: "ราคาต่อหน่วย",
    field_transactionCode: "รหัสประเภทธุรกรรมภาษี",
    field_etaxRegistered: "การยืนยันว่าเป็นผู้มีสิทธิออก e-Tax / e-Receipt",
    field_foreignCurrencyApproval: "การอนุมัติให้ใช้ภาษาหรือสกุลเงินต่างประเทศ",
    field_priceIncludesTaxNote: "ข้อความราคารวมภาษี",
    required_field_title: "ยังขาดฟิลด์บังคับ",
    required_field_summary: "กรุณาเพิ่ม {field} ก่อนออกเอกสาร",
    required_field_action: "กรอก {field} แล้วตรวจสอบอีกครั้ง",
    format_title: "รูปแบบเลขทะเบียน/เลขภาษีไม่ถูกต้อง",
    format_summary: "{field} ไม่ตรงกับรูปแบบที่คาดไว้สำหรับ {country}",
    format_action: "ตรวจสอบเลขดังกล่าวกับทะเบียนทางการอีกครั้ง",
    tax_id_required_title: "ต้องแสดงเลขภาษี",
    tax_id_required_summary: "เอกสารนี้มีการคิด {taxLabel} จึงต้องแสดงเลขภาษีของผู้ขาย",
    tax_id_required_action: "กรอกเลขภาษีผู้ขาย หรือเปลี่ยนเป็นโหมดที่ยังไม่จดทะเบียน",
    tax_rate_title: "อัตราภาษีไม่ตรงกับกฎของประเทศ",
    tax_rate_summary: "อัตราที่ควรเป็นคือ {expectedRate}% แต่พบ {actualRate}%",
    tax_rate_action: "ใช้อัตรามาตรฐาน กรณีอัตรา 0% ที่รองรับ หรือเอาภาษีออกหากยังไม่จดทะเบียน",
    total_mismatch_title: "ยอดรวมไม่สอดคล้องกัน",
    total_mismatch_summary: "ยอดในรายการและยอดรวมต่างกัน {difference}",
    total_mismatch_action: "คำนวณยอดรายการ ยอดภาษี ส่วนลด และการปัดเศษใหม่",
    duplicate_title: "พบเลขที่เอกสารซ้ำ",
    duplicate_summary: "ผู้ขายรายนี้เคยใช้เลขที่เอกสาร {invoiceNumber} แล้ว",
    duplicate_action: "ใช้เลขที่เอกสารใหม่ที่ไม่ซ้ำ หรือแก้ไขจากรายการเดิมแทน",
    cross_border_title: "ธุรกรรมข้ามพรมแดนต้องมีหลักฐานประกอบ",
    cross_border_summary: "การใช้อัตรา 0% หรือวิธีปฏิบัติด้านส่งออกที่เลือกต้องมีเอกสารสนับสนุน",
    cross_border_action: "เก็บสัญญา หลักฐานลูกค้าต่างประเทศ และหลักฐานการชำระเงินตามที่เกี่ยวข้อง",
    threshold_near_title: "ใกล้ถึงเกณฑ์การจดทะเบียน",
    threshold_near_summary: "เกณฑ์ {taxLabel} คือ {threshold}; รายได้ปัจจุบันคือ {current}",
    threshold_near_action: "ติดตามรายได้อย่างใกล้ชิดและเตรียมข้อมูลจดทะเบียนล่วงหน้า",
    threshold_above_title: "เกินเกณฑ์การจดทะเบียนแล้ว",
    threshold_above_summary: "เกณฑ์ {taxLabel} คือ {threshold}; รายได้ปัจจุบันคือ {current}",
    threshold_above_action: "เริ่มขั้นตอนการจดทะเบียนและใช้งานรูปแบบเอกสารสำหรับผู้จดทะเบียนภาษี",
    threshold_future_title: "รายได้คาดการณ์จะก่อให้เกิดหน้าที่จดทะเบียน",
    threshold_future_summary: "เกณฑ์ {taxLabel} คือ {threshold}; รายได้คาดการณ์คือ {projected}",
    threshold_future_action: "เตรียมเอกสารสนับสนุนและวางแผนวันที่มีผลของการจดทะเบียน",
    note_title: "ข้อสังเกตเฉพาะประเทศ",
    note_summary: "{message}",
    note_action: "ตรวจทานหมายเหตุนี้และยืนยันว่าถ้อยคำในเอกสารถูกต้องตามข้อกำหนดทางการ",
    threshold_status_below: "ต่ำกว่าเกณฑ์",
    threshold_status_near: "ใกล้ถึงเกณฑ์",
    threshold_status_above: "เกินเกณฑ์",
    threshold_status_future: "จะถึงเกณฑ์ตามคาดการณ์",
    threshold_status_na: "ไม่เกี่ยวข้อง",
    country_SG: "สิงคโปร์",
    country_MY: "มาเลเซีย",
    country_ID: "อินโดนีเซีย",
    country_TH: "ประเทศไทย",
    profile_sg_standard: "ใบกำกับภาษี GST สิงคโปร์",
    profile_sg_simplified: "ใบกำกับภาษีอย่างย่อสิงคโปร์",
    profile_sg_non_registered: "ใบแจ้งหนี้เชิงพาณิชย์สิงคโปร์",
    profile_my_einvoice: "ร่าง e-Invoice มาเลเซีย",
    profile_my_sst: "ใบกำกับ SST มาเลเซีย",
    profile_my_non_registered: "ใบแจ้งหนี้เชิงพาณิชย์มาเลเซีย",
    profile_id_pkp: "ร่าง e-Faktur PKP อินโดนีเซีย",
    profile_id_retail: "ใบกำกับภาษีค้าปลีกอินโดนีเซีย",
    profile_id_non_registered: "ใบแจ้งหนี้เชิงพาณิชย์อินโดนีเซีย",
    profile_th_vat: "ใบกำกับภาษี VAT ไทย",
    profile_th_non_registered: "ใบแจ้งหนี้เชิงพาณิชย์ไทย",
    profile_th_etax: "ร่าง e-Tax Invoice / e-Receipt ไทย",
    profile_th_abbreviated: "ใบกำกับภาษีอย่างย่อไทย"
  }
};

export const getDictionary = (locale: LocaleCode): Dictionary => dictionaries[locale] ?? dictionaries.en;

export const t = (
  locale: LocaleCode,
  key: string,
  params: Record<string, string | number> = {}
): string => {
  const dictionary = getDictionary(locale);
  const template = dictionary[key] ?? dictionaries.en[key] ?? key;
  return Object.entries(params).reduce((current, [paramKey, paramValue]) => {
    return current.replaceAll(`{${paramKey}}`, String(paramValue));
  }, template);
};

export const text = (
  en: string,
  zhCN: string,
  msMY: string,
  idID: string,
  thTH?: string
): LocalizedText => ({
  en,
  "zh-CN": zhCN,
  "ms-MY": msMY,
  "id-ID": idID,
  "th-TH": thTH ?? en
});

export const getCountryName = (locale: LocaleCode, country: CountryCode): string => t(locale, `country_${country}`);

